import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, X as XIcon } from 'lucide-react';
import { useRealtimeJobs } from '../hooks/useRealtimeJobs';
import type { ActiveJob } from '../api/types';
import { formatDistanceToNowStrict } from 'date-fns';
import { ko } from 'date-fns/locale';

// 권위: docs/ARCH_DASHBOARD.md §4.0.3
// 상태: Idle / Running(펄스) / Error / Stale.
// dismiss: 종료된(completed/failed/stale) 항목을 운영자가 수동으로 가릴 수 있다 (localStorage 기반).
export function RealtimeWorkWidget(): JSX.Element {
  const { activeJobs, recentJobs, dismissJob, dismissFinished, dismissibleCount } = useRealtimeJobs();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const runningCount = activeJobs.filter((j) => j.status === 'running').length;
  const staleCount = activeJobs.filter((j) => j.status === 'stale').length;
  const failedCount = recentJobs.filter((j) => j.status === 'failed').length;

  let badge: 'idle' | 'running' | 'error' | 'stale' = 'idle';
  let label = 'Idle';
  if (failedCount > 0) {
    badge = 'error';
    label = `문제 ${failedCount}건`;
  } else if (staleCount > 0) {
    badge = 'stale';
    label = `지연 ${staleCount}건`;
  } else if (runningCount > 0) {
    badge = 'running';
    label = `실행 중: ${runningCount}`;
  }

  return (
    <div className="realtime-work-widget" ref={containerRef}>
      <button
        type="button"
        className={`realtime-work-widget__trigger realtime-work-widget__trigger--${badge}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={`실시간 작업: ${label}`}
      >
        <Zap size={16} aria-hidden />
        <span>{label}</span>
      </button>
      {open && (
        <div className="realtime-work-widget__dropdown" role="dialog" aria-label="실시간 작업">
          <div className="realtime-work-widget__header">
            <span>실시간 작업 (진행 {activeJobs.length} · 최근 {recentJobs.length})</span>
            {dismissibleCount > 0 && (
              <button
                type="button"
                className="realtime-work-widget__ack-all"
                onClick={dismissFinished}
                title="화면에 보이는 종료된 항목을 모두 인지 처리"
              >
                모두 인지
              </button>
            )}
          </div>
          <section>
            <h3>▶ 진행 중</h3>
            {activeJobs.length === 0 && <p className="realtime-work-widget__empty">현재 실행 중인 작업이 없습니다.</p>}
            <ul>
              {activeJobs.map((j) => (
                <li key={j.jobId}>
                  <JobRow
                    job={j}
                    onClick={() => navigateToHistory(j, navigate, setOpen)}
                    onDismiss={() => dismissJob(j.jobId)}
                  />
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3>✓ 최근 완료</h3>
            {recentJobs.length === 0 && <p className="realtime-work-widget__empty">최근 완료된 작업이 없습니다.</p>}
            <ul>
              {recentJobs.map((j) => (
                <li key={j.jobId}>
                  <JobRow
                    job={j}
                    onClick={() => navigateToHistory(j, navigate, setOpen)}
                    onDismiss={() => dismissJob(j.jobId)}
                  />
                </li>
              ))}
            </ul>
          </section>
          <div className="realtime-work-widget__footer">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/history');
              }}
            >
              전체 이력 보기 →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function navigateToHistory(
  job: ActiveJob,
  navigate: ReturnType<typeof useNavigate>,
  setOpen: (v: boolean) => void
): void {
  setOpen(false);
  const isTrace = job.jobType.startsWith('LLM_GATE_');
  const paramName = isTrace ? 'trace_id' : 'run_id';
  navigate(`/history?${paramName}=${encodeURIComponent(job.jobId)}`);
}

function JobRow({
  job,
  onClick,
  onDismiss,
}: {
  job: ActiveJob;
  onClick: () => void;
  onDismiss: () => void;
}): JSX.Element {
  const when = (() => {
    try {
      return formatDistanceToNowStrict(new Date(job.startedAt), { locale: ko, addSuffix: true });
    } catch {
      return job.startedAt;
    }
  })();
  const icon = statusIcon(job.status);
  // running 중인 작업은 강제 숨김 방지 — dismiss 버튼 미노출.
  const canDismiss = job.status !== 'running';
  return (
    <div className="realtime-work-widget__row-wrap">
      <button type="button" className="realtime-work-widget__row" onClick={onClick}>
        <span className={`realtime-work-widget__status-dot realtime-work-widget__status-dot--${job.status}`}>{icon}</span>
        <span className="realtime-work-widget__row-title">
          {job.title}
          {job.currentStep ? ` ${job.currentStep}` : ''}
        </span>
        <span className="realtime-work-widget__row-meta">
          {job.jobId.slice(0, 16)} · {when}
        </span>
      </button>
      {canDismiss && (
        <button
          type="button"
          className="realtime-work-widget__dismiss"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          aria-label={`${job.title} 인지 처리`}
          title="인지 처리 (목록에서 숨김)"
        >
          <XIcon size={14} strokeWidth={1.75} aria-hidden />
        </button>
      )}
    </div>
  );
}

function statusIcon(status: ActiveJob['status']): string {
  switch (status) {
    case 'running':
      return '⚡';
    case 'completed':
      return '🟢';
    case 'failed':
      return '🔴';
    case 'stale':
      return '🟠';
    case 'skipped':
      return '⏭️';
  }
}
